import React, { useState, useEffect } from "react";
import styled from "@emotion/styled";
import { SmallText } from "../../../components/core/Utility";
import { Tooltip } from "primereact/tooltip";
import FailedIcon from "../../assets/icons/ErrorIcon.svg";
import InProgressImg from "../../assets/icons/InProgress.svg";
import ErrorImg from "../../assets/icons/Error.svg";
import ReleasedImg from "../../assets/icons/Released.svg";
import ShippedImg from "../../assets/icons/Shipped.png";
import CancelImg from "../../assets/icons/Cancelled.svg";
import BackorderedImg from "../../assets/icons/Backordered.svg";
import ReturnSubmittedImg from "../../assets/icons/ReturnSubmitted.svg";
import SubmittedImg from "../../assets/icons/Submitted.svg";
import CreatedImg from "../../assets/icons/Created.svg";
import PackagingImg from "../../assets/icons/Packaging.svg";
import DeliveredImg from "../../assets/icons/Delivered.svg";
import { Icon } from "@vds/icons";
import "primeicons/primeicons.css";
import DoNotShipImg from "../../assets/icons/DoNotShip.svg";
const rowsDefault = [{ row: "" }];

const TableContainer = styled.div`
  font-family: Verizonn-NHG-eTx, Helvetica, Arial, sans-serif;
  border-radius: 10px;
  width: 100%;
  overflow-x: auto;
  border-bottom: none;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 0.85em;
  margin-bottom: 0;
  border-bottom: none;

  th {
    background-color: #f8f9fa;
    padding: 2px 8px;
    text-align: left;
    font-weight: bold;
    border: none;
  }

  td {
    padding: 2px 8px;
    vertical-align: top;
    border-bottom: none;
    line-height: 1.1;
  }

  tr.last-in-group td {
    padding-bottom: 20px !important;
    border-bottom: none;
  }

  tr.last-row td {
    padding-bottom: 10px !important;
  }

  tr {
    margin: 0;
    height: auto;
    min-height: 0;
  }

  * {
    margin: 0;
    padding: 0;
  }

  margin-bottom: 10px;
`;

const VehicleCell = styled.td`
  font-weight: bold;
`;

const StyledIconContainer = styled.div`
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0;
`;

const IconWrapper = styled.div`
  height: 18px;
  width: 18px;
  align-items: center;
  cursor: pointer;
  display: flex;
  margin-left: 2px;
  margin-bottom: 0;
  padding: 0;
`;

const HardwareDisplayContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0;
  padding: 0;
  width: 100%;
  min-height: 20px;
`;

const ShipmentHoldIcon = styled.div`
  height: 18px;
  width: 18px;
  align-items: center;
  cursor: pointer;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  margin: 0;
  padding: 0;
`;

const TextWrapper = styled.div`
  align-items: center;
  cursor: pointer;
  display: flex;
`;

const isClosedLineItem = (materialObj) => {
  return materialObj.status?.toLowerCase() === "submitted";
};

export const AssignHardwareGrid = ({
  noDataMessage = "No data to display",
  rows = { rowsDefault },
  editVehicle,
  isVehicleEditable,
  bundleName,
  bundleId,
  shipGroupsData,
  assetType,
  showActionIcon,
  disableEditDelete,
}) => {
  const renderVehicleCell = (row) => {
    const isEditable = isVehicleEditable(row) && !disableEditDelete;
    return (
      <div>
        <SmallText
          bold
          css={{
            textDecoration: isEditable ? "underline" : "none",
            cursor: isEditable ? "pointer" : "not-allowed",
          }}
          onClick={() => {
            editVehicle(row, bundleName, bundleId, shipGroupsData, assetType);
          }}
        >
          {row.year} {row.make} {row.model}
        </SmallText>
        {row.displayVinId && <SmallText>{row.vehicle}</SmallText>}
        <SmallText bold css={{ marginTop: "30px" }}>
          {row.sourceApplication}
        </SmallText>
        {row.isShippingBlocked && <SmallText bold>Hold Vehicle</SmallText>}
      </div>
    );
  };

  const renderTraits = (row) => {
    return (
      <div>
        <div>
          {row.wheelChair && (
            <Icon
              className="trash-icon"
              aria-label="InternationalSymbolOfAccess"
              alt="InternationalSymbolOfAccess"
              name="international-symbol-of-access"
              size="XLarge"
              style={{ outline: "none", width: "1.3rem", blockSize: "1.3rem" }}
              color="#000000"
            />
          )}
          {row.emergency && (
            <Icon
              className="police-fleet"
              aria-label="police-fleet"
              alt="police-fleet"
              name="police-fleet"
              size="Large"
              color="#000000"
              style={{ outline: "none", width: "1.3rem", blockSize: "1.3rem" }}
            />
          )}
          {row.killSwitch && (
            <Icon
              className="smart-switch"
              aria-label="smart-switch"
              alt="smart-switch"
              name="smart-switch"
              size="Large"
              color="#000000"
              style={{ outline: "none", width: "1.3rem", blockSize: "1.3rem" }}
            />
          )}
        </div>
        <SmallText>{row.weightClass}</SmallText>
      </div>
    );
  };

  const renderVehicleStatus = (row) => {
    return (
      <div>
        {row.vehicleStatus === "Warning" && row?.comment?.length > 0 && (
          <StyledIconContainer>
            <IconWrapper css={{ marginLeft: "-15px" }}>
              <Tooltip target=".errorlogo" mouseTrack mouseTrackLeft={10} />
              <img
                alt="Error icon"
                className="errorlogo"
                src={FailedIcon}
                height="16px"
                width="16px"
                aria-label="Error"
                data-pr-tooltip={row?.comment}
              ></img>
            </IconWrapper>
            <SmallText>Needs Attention</SmallText>
          </StyledIconContainer>
        )}

        {row.vehicleStatus === "Warning" && row?.comment == null && (
          <StyledIconContainer>
            <SmallText>Needs Attention</SmallText>
          </StyledIconContainer>
        )}

        {row.vehicleStatus === "Ready" && row?.comment?.length > 0 && (
          <StyledIconContainer>
            <IconWrapper css={{ marginLeft: "-15px" }}>
              <Tooltip target=".errorlogo" mouseTrack mouseTrackLeft={10} />
              <img
                alt="Error icon"
                className="errorlogo"
                src={FailedIcon}
                height="16px"
                width="16px"
                css={{ visibility: row?.comment?.length > 0 ? "visible" : "hidden" }}
                aria-label="Error"
                data-pr-tooltip={row?.comment}
              ></img>
            </IconWrapper>
            <TextWrapper>
              <Tooltip target=".info" mouseTrack mouseTrackLeft={10} />
              {Number(row.priority) === 1 ? (
                <SmallText data-pr-tooltip="Hardware Recommended" className="info">
                  {row.vehicleStatus}
                </SmallText>
              ) : (
                <SmallText data-pr-tooltip="Hardware Specified" className="info">
                  {row.vehicleStatus}
                </SmallText>
              )}
            </TextWrapper>
          </StyledIconContainer>
        )}

        {row.vehicleStatus === "Ready" && row?.comment == null && (
          <StyledIconContainer>
            <TextWrapper>
              <Tooltip target=".info" mouseTrack mouseTrackLeft={10} />
              {Number(row.priority) === 1 ? (
                <SmallText data-pr-tooltip="Hardware Recommended" className="info">
                  {row.vehicleStatus}
                </SmallText>
              ) : (
                <SmallText data-pr-tooltip="Hardware Specified" className="info">
                  {row.vehicleStatus}
                </SmallText>
              )}
            </TextWrapper>
          </StyledIconContainer>
        )}
      </div>
    );
  };

  if (!rows?.length) {
    return <SmallText>{noDataMessage}</SmallText>;
  }

  return (
    <TableContainer>
      <StyledTable>
        <thead>
          <tr>
            <th style={{ width: "18%" }}>
              <SmallText bold>Vehicle</SmallText>
            </th>
            <th style={{ width: "8%" }}>
              <SmallText bold>Traits</SmallText>
            </th>
            <th style={{ width: "8%" }}>
              <SmallText bold>Vehicle Status</SmallText>
            </th>
            <th style={{ width: "10%" }}>
              <SmallText bold>Category</SmallText>
            </th>
            <th style={{ width: "18%" }}>
              <SmallText bold>Subscription</SmallText>
            </th>
            <th style={{ width: "15%" }}>
              <SmallText bold>Tech Ref ID</SmallText>
            </th>
            <th style={{ width: "11%" }}>
              <SmallText bold>Type</SmallText>
            </th>
            <th style={{ width: "26%" }}>
              <SmallText bold>Hardware</SmallText>
            </th>
            <th style={{ width: "16%" }}>
              <SmallText bold>WorkTicket</SmallText>
            </th>
            <th style={{ width: "12%" }}>
              <SmallText bold>Delivery Date</SmallText>
            </th>
            <th style={{ width: "5%" }}></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((vehicle, vehicleIndex) => {
            const isEvenVehicle = vehicleIndex % 2 === 1;
            const groupBackgroundColor = isEvenVehicle ? "rgb(240, 240, 240)" : "white";
            const isLastRow = vehicleIndex === rows.length - 1;

            if (!vehicle.materialMapping || !vehicle.materialMapping.length) {
              return (
                <tr
                  key={`${vehicle.vehicle}-empty`}
                  style={{ backgroundColor: groupBackgroundColor }}
                  className={`vehicle-row ${isLastRow ? "last-row" : ""}`}
                >
                  <VehicleCell>{renderVehicleCell(vehicle)}</VehicleCell>
                  <td>{renderTraits(vehicle)}</td>
                  <td>{renderVehicleStatus(vehicle)}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td style={{ textAlign: "center", verticalAlign: "middle" }}>{showActionIcon(vehicle)}</td>
                </tr>
              );
            }
            return vehicle.materialMapping.map((material, materialIndex) => {
              const isFirstMaterial = materialIndex === 0;
              const isLastMaterial = materialIndex === vehicle.materialMapping.length - 1;
              const isLastInGroup = isLastMaterial;
              const textColor = !isClosedLineItem(material) ? "black" : "grey";
              return (
                <tr
                  key={`${vehicle.vehicle}-${material.subscriptionId}-${materialIndex}`}
                  style={{ backgroundColor: groupBackgroundColor }}
                  className={`
                    ${materialIndex === 0 ? "vehicle-row" : ""} 
                    ${isLastInGroup && !isLastRow ? "last-in-group" : ""} 
                    ${isLastRow && isLastMaterial ? "last-row" : ""}
                  `}
                >
                  {isFirstMaterial ? (
                    <>
                      <VehicleCell rowSpan={vehicle.materialMapping.length}>{renderVehicleCell(vehicle)}</VehicleCell>
                      <td rowSpan={vehicle.materialMapping.length}>{renderTraits(vehicle)}</td>
                      <td rowSpan={vehicle.materialMapping.length}>{renderVehicleStatus(vehicle)}</td>
                    </>
                  ) : null}
                  <td>
                    <p style={{ color: textColor }}>{(material.type || "") + " - " + (material.subType || "")}</p>
                  </td>
                  <td>
                    <p style={{ color: textColor }}>
                      {material.subscriptionName ? `${material.subscriptionName}(${material.subscriptionId || ""})` : ""}
                    </p>
                  </td>
                  <td>
                    <p style={{ color: textColor }}>{material.techRefId || ""}</p>
                  </td>
                  <td>
                    <p style={{ color: textColor }}>{material?.lineType || "--"}</p>
                  </td>
                  <td>
                    <HardwareDisplayContainer>
                      <p style={{ color: textColor, flex: 1, overflow: "hidden" }}>
                        {material.hardwareName || ""}
                        {" - "}
                        {material.hardwareId ? `${material.hardwareId}${material.included ? " (Included)" : ""}` : ""}
                      </p>

                      {material?.isShipmentHold && (
                        <ShipmentHoldIcon>
                          <Tooltip target=".errorlogo" mouseTrack mouseTrackLeft={10} />
                          <img
                            alt="Do Not Ship icon"
                            className="errorlogo"
                            src={DoNotShipImg}
                            height="16px"
                            width="16px"
                            aria-label="DoNotShip"
                            data-pr-tooltip={"Do Not Ship"}
                          />
                        </ShipmentHoldIcon>
                      )}
                    </HardwareDisplayContainer>
                  </td>
                  <td>
                    <StyledIconContainer>
                      <p style={{ color: textColor }}>
                        {!((material.status === "Ready" || material.status === "Warning") && material.wtstatus?.toLowerCase() !== "cancelled")
                          ? (material.workTicketId || "--") + " "
                          : "--"}
                      </p>
                      {!((material.status === "Ready" || material.status === "Warning") && material.wtstatus?.toLowerCase() !== "cancelled") && (
                        <IconWrapper css={{ marginLeft: "5px" }}>
                          <Tooltip target=".errorlogo" mouseTrack mouseTrackLeft={10} />
                          {renderImage(material) && (
                            <img
                              alt="Status icon"
                              className="errorlogo"
                              src={renderImage(material)}
                              height="16px"
                              width="16px"
                              aria-label="Status"
                              data-pr-tooltip={material?.wtstatus || ""}
                            />
                          )}
                        </IconWrapper>
                      )}
                    </StyledIconContainer>
                  </td>
                  <td>
                    <p style={{ color: textColor }}>{material?.deliveryDate || "--"}</p>
                  </td>
                  {isFirstMaterial ? (
                    <td
                      rowSpan={vehicle.materialMapping.length}
                      style={{
                        textAlign: "center",
                        verticalAlign: "middle",
                        paddingBottom: "20px",
                      }}
                    >
                      {vehicle?.materialMapping?.every((material) => !material?.lineType) && showActionIcon(vehicle)}
                    </td>
                  ) : null}
                </tr>
              );
            });
          })}
        </tbody>
      </StyledTable>
    </TableContainer>
  );
};

export const renderImage = (data) => {
  let workTicketStatus = data?.wtstatus ? data?.wtstatus?.toLowerCase() : "";
  let statusIcon;
  switch (workTicketStatus) {
    case "error":
      statusIcon = ErrorImg;
      break;
    case "released":
      statusIcon = ReleasedImg;
      break;
    case "shipped":
      statusIcon = ShippedImg;
      break;
    case "cancelled":
      statusIcon = CancelImg;
      break;
    case "return submitted":
      statusIcon = ReturnSubmittedImg;
      break;
    case "backordered":
      statusIcon = BackorderedImg;
      break;
    case "submitted":
      statusIcon = SubmittedImg;
      break;
    case "created":
      statusIcon = CreatedImg;
      break;
    case "packaging":
      statusIcon = PackagingImg;
      break;
    case "delivered":
      statusIcon = DeliveredImg;
      break;
    default:
      statusIcon = null;
  }
  return statusIcon;
};
